import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { addToCart, type CartItem } from '../../utils/cart';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';
import RelatedProducts from './components/RelatedProducts';
import ReviewSection from './components/ReviewSection';
import { useProduct } from '../../hooks/useProduct';
import { useProducts } from '../../hooks/useProducts';
import { useSEO, generateProductSchema, generateBreadcrumbSchema } from '../../utils/seo';
import CartNotification from '../../components/feature/CartNotification';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [notifiedItem, setNotifiedItem] = useState<CartItem | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const { product, loading, error } = useProduct(id);
  const { products: allProducts } = useProducts();

  const relatedProducts = allProducts
    .filter((p) => p.category === product?.category && p.id !== product?.id)
    .slice(0, 3);

  useSEO({
    title: product ? `${product.name} | Daliah Secret` : 'Product | Daliah Secret',
    description: product?.description || 'Luxury handcrafted perfume and hair oil product',
    keywords: product ? `${product.name}, ${product.category}, luxury perfume, hair oil, Daliah Secret` : 'luxury perfume, hair oil',
    canonical: `/product/${id}`,
    ogType: 'product',
    ogImage: product?.image,
    schema: product ? {
      '@graph': [
        generateProductSchema(product),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Products', url: '/products' },
          { name: product.name, url: `/product/${id}` },
        ]),
      ],
    } : undefined,
  });

  const handleAddToCart = (qty: number) => {
    if (!product) return;
    const result = addToCart(
      {
        id: parseInt(product.id),
        name: product.name,
        price: product.price,
        image: product.image,
      },
      qty,
      product.stock_quantity
    );

    if (result.success && result.item) {
      setNotifiedItem(result.item);
    } else {
      setToastMessage(result.message || 'Erreur lors de l\'ajout au panier');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-28 pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Loading Skeleton */}
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-64 mb-10"></div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-20">
                <div className="bg-gray-200 rounded-2xl h-64 sm:h-96 lg:h-[600px]"></div>
                <div className="space-y-6">
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-12 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-24 text-center">
          <i className="ri-error-warning-line text-6xl text-gray-300 mb-6 block"></i>
          <h2 className="text-3xl font-serif text-gray-900 mb-4">Produit non trouvé</h2>
          <p className="text-gray-600 mb-8">
            {error || 'Le produit que vous recherchez n\'existe pas.'}
          </p>
          <button
            onClick={() => navigate('/products')}
            className="px-8 py-3 bg-[#2E7D32] text-white font-medium rounded-full hover:bg-black hover:text-white transition-all cursor-pointer whitespace-nowrap"
          >
            RETOUR À LA BOUTIQUE
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-24 right-4 z-50 px-6 py-4 rounded-lg shadow-lg animate-slide-in ${
          toastType === 'error' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          <div className="flex items-center gap-3 text-white">
            <i className={`${toastType === 'error' ? 'ri-error-warning-line' : 'ri-check-line'} text-xl`}></i>
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      <div className="pt-28 pb-12 sm:pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 sm:mb-10 overflow-x-auto">
            <Link to="/" className="hover:text-[#2E7D32] transition-colors cursor-pointer whitespace-nowrap">Home</Link>
            <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
            <Link to="/products" className="hover:text-[#2E7D32] transition-colors cursor-pointer whitespace-nowrap">Products</Link>
            <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
            <span className="text-gray-900 font-medium truncate">{product.name}</span>
          </nav>

          {/* Product Hero */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-12 sm:mb-16 lg:mb-20">
            {/* Image */}
            <div className="relative">
              {product.status && (
                <span className="absolute top-4 left-4 z-10 px-4 py-1.5 bg-[#2E7D32] text-white text-xs font-bold tracking-wider rounded-full">
                  {product.status}
                </span>
              )}
              <div className="bg-[#F1F8E9] rounded-2xl overflow-hidden h-64 sm:h-96 lg:h-[600px]">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <ProductInfo product={product} onAddToCart={handleAddToCart} />
          </div>

          {/* Tabs */}
          <ProductTabs product={product} />

          {/* Customer Reviews */}
          <ReviewSection
            productId={Number(product.id)}
            productRating={product.rating}
            reviewCount={product.review_count}
          />

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <RelatedProducts products={relatedProducts} />
          )}
        </div>
      </div>

      {/* Cart Notification */}
      <CartNotification item={notifiedItem} onClose={() => setNotifiedItem(null)} />

      <Footer />
    </div>
  );
}
